import type { TokenInfo } from '@/stores/interface/oauth2Store.d'
import { useOAuth2Store } from '@/stores/oauth2Store'
import { useSecurityStore } from '@/stores/securityStore'

/**
 * Calculate token expiration time in minutes
 * @param tokenInfo - Token information object
 * @returns Minutes until expiration, or -1 if tokenInfo is invalid
 */
const calculateExpirationTime = (tokenInfo: TokenInfo | null | undefined): number => {
  console.log('start to calculate token expiration in minutes', tokenInfo)

  if (!tokenInfo) {
    console.log('no token found, return.')
    return -1
  }

  const expireTime = tokenInfo.expireTime

  if (!expireTime) {
    console.log('no expiration time found, return.')
    return -1
  }

  const diff = expireTime - new Date().getTime()
  const mins = Math.floor(diff / 1000 / 60)

  console.log('expire in mins:', mins)

  return mins
}

/**
 * Get token expiration time in minutes, automatically refresh if needed
 * @param tokenInfo - Token information object
 * @returns Promise resolving to minutes until expiration
 */
export const getTokenExpirationInMinutes = (tokenInfo: TokenInfo): Promise<number> => {
  const oauth2Store = useOAuth2Store()
  const mins = calculateExpirationTime(tokenInfo)

  const rt = oauth2Store.getterRefreshToken

  if (mins < 2 && rt) {
    return oauth2Store
      .doRefreshToken()
      .then(ti => {
        return calculateExpirationTime(ti)
      })
      .catch(error => {
        return Promise.reject(new Error(String(error)))
      })
  }

  return Promise.resolve(mins)
}

/**
 * Get access token, automatically refresh if needed.
 * Triggers refresh when the access token expires within 2 minutes,
 * ensuring the request interceptor always attaches a valid token.
 */
export const getAccessToken = (tokenInfo: TokenInfo | null): Promise<string> => {
  const oauth2Store = useOAuth2Store()
  const mins = calculateExpirationTime(tokenInfo)

  const rt = oauth2Store.getterRefreshToken

  if (mins < 2 && rt) {
    return oauth2Store
      .doRefreshToken()
      .then(ti => {
        return ti.accessToken
      })
      .catch(error => {
        return Promise.reject(new Error(String(error)))
      })
  }

  return Promise.resolve(tokenInfo?.accessToken || '')
}

/**
 * Check if user has access based on roles
 * @param rolesDefinitions - Array of role definitions to check against
 * @returns True if user has access, false otherwise
 */
export const hasAccess = (rolesDefinitions?: string[]): boolean => {
  const oauth2Store = useOAuth2Store()

  if (!rolesDefinitions) {
    // no access control
    return true
  }

  const tokenInfo = oauth2Store.getterTokenInfo

  if (!tokenInfo?.token) {
    return false
  }

  const currentRoles = tokenInfo.token.roles

  if (!currentRoles || !Array.isArray(currentRoles)) {
    return false
  }

  for (const currentRole of currentRoles) {
    for (const rolesDefinition of rolesDefinitions) {
      if (currentRole === rolesDefinition) {
        return true
      }
    }
  }

  return false
}

/**
 * Reload menu and permission data
 * @returns Promise resolving when both operations complete
 */
export const reloadMenuPermission = async (): Promise<void> => {
  const securityStore = useSecurityStore()
  console.log('reloadMenuPermission start')
  const menus = await securityStore.generateMenus()
  const permissions = await securityStore.generatePermissions()
  console.log('reloadMenuPermission end')
  await Promise.all([menus, permissions])
}
